import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { UserPlus } from 'lucide-react';
import { ToastType, useToast } from '../toast/ToastProvider';
import LeadService from '../../services/leadService';

import { Lead } from '../../types/Lead';
import { UtilsService } from '../../services/utilsService';
import ConfirmModal from '../../modals/ConfirmModal';
import { useTranslation } from 'react-i18next';
import { WhatsAppMessageTypes } from '../../types/WhatsAppMessage';
import WhatsAppApiService from '../../services/whatsAppApiService';


const Leads = () => {
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedLeadIndex, setSelectedLeadIndex] = useState<number>(-1);
    const [leads, setLeads] = useState<Lead[]>([]);
    const [showDelteConfirm, setShowDeleteConfirm] = useState(false);
    const { showToast } = useToast();
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'he';
    const activeClinicId = sessionStorage.getItem('activeClinicId') || '';

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        birthDate: '',
        email: '',
        source: ''
    });

    useEffect(() => {
        fetchLeads();
    }, []);
    
    const fetchLeads = async () => {
        const controller = new AbortController();

        try {
            const leadsData = await LeadService.getAllLeads(controller.signal);
            const formattedLeads = UtilsService.formatDateField(leadsData, 'birthDate');

            setLeads(formattedLeads); 
        } catch (error:any) {
            const code = error.response?.data?.code || 'UNKNOWN_ERROR';
            showToast(t(`leadMessages.${code}`), ToastType.Error);
        }
    };

    const handleAddLead = async () => {
        const controller = new AbortController(); // Create an AbortController

        try {
            
            if(!UtilsService.validatePhoneNumber(formData.phone)){
                showToast(t(`toastMessages.FILL_VALID_PHONE`), ToastType.Warning);
                return null;
            }
            
            const newLead = await LeadService.createLead({ ...formData, clinicId: activeClinicId }, controller.signal);
    
            if (newLead?.error) { 
                
                const code = newLead?.error?.response?.data?.code || 'UNKNOWN_ERROR';
                showToast(t(`leadMessages.${code}`), ToastType.Warning);
                return;
            }
    
            setLeads(prevLeads => [...prevLeads, newLead.savedLead]);
            showToast(t(`leadMessages.${newLead.code}`), ToastType.Success);
    
            setShowInviteModal(false);
            resetForm();
        } catch (error: any) {
            const code = error?.response?.data?.code || 'UNKNOWN_ERROR';
            showToast(t(`leadMessages.${code}`), ToastType.Error);
        }
    };
        
    const handleEditLead = (index: number) => {
        setSelectedLeadIndex(index);
        setFormData(leads[index]);
        setShowEditModal(true);
    };

    const handleSaveEdit = async () => {
        if (selectedLeadIndex === null) return;
        const leadId = leads[selectedLeadIndex]._id;

        if (!leadId) {
            showToast(t('invalid-lead-id'), ToastType.Error);
            return;
        }

        try {
            if (!UtilsService.validatePhoneNumber(formData.phone)) {
                showToast(t(`toastMessages.FILL_VALID_PHONE`), ToastType.Warning);
                return null;
            }

            const updatedLead = await LeadService.updateLead(leadId, formData);

            if (updatedLead?.error) { 
                showToast(updatedLead?.message, ToastType.Warning);
                return;
            }

            const formattedLead = UtilsService.formatDateField(updatedLead.lead, 'birthDate');

            setLeads((prev) => {
                const updatedList = [...prev];
                updatedList[selectedLeadIndex] = formattedLead;
                return updatedList;
            });
            showToast(t('lead-update-succees'), ToastType.Success);
            setShowEditModal(false);
            resetForm();
        } catch (error: any) {
            const code = error?.response?.data?.code || 'UNKNOWN_ERROR';
            showToast(t(`leadMessages.${code}`), ToastType.Error);
        }
    };

    
    const handleDelete = (index: number) => {
        setSelectedLeadIndex(index);
        setShowDeleteConfirm(true);
    };

    const handleDeleteLead = async (index: number) => {
        
        const leadId = leads[index]._id;

        if (!leadId) {
            showToast(t('invalid-lead-id'), ToastType.Error);
            return;
        }

        try {
            const deleteResult = await LeadService.deleteLead(leadId);
            setLeads((prev) => prev.filter((_, i) => i !== index));
            showToast(t(`leadMessages.${deleteResult?.code}`), ToastType.Success);
        } catch (error: any) {
            const code = error?.response?.data?.code || 'UNKNOWN_ERROR';
            showToast(t(`leadMessages.${code}`), ToastType.Error);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const resetForm = () => {
        setFormData({ firstName: '', lastName: '', phone: '', birthDate: '', email: '', source: '' });
    };

    const confirmDelete = () => {
        if (selectedLeadIndex != null) {
            handleDeleteLead(selectedLeadIndex);
        }

        setShowDeleteConfirm(false);
    };

    const cancelDelete = () => {
        setShowDeleteConfirm(false);
    };

    const getDeleteConfirmationText = () => {
        const selectedLead = leads[selectedLeadIndex];

        return (
            <>
                {t('are-you-sure-delete-lead')}
                <br />
                <strong>{t('lead')}:</strong>{' '}
                {selectedLead ? `${selectedLead.firstName} ${selectedLead.lastName}` : ''}
                <br />
                {t('this-action-cannot-be-undone')}
            </>
        );
    };

    const handleSendLeadMessage = async (index: number) => {

        const leadId = leads[index]._id;

        if (!leadId) {
            showToast(t('invalid-lead-id'), ToastType.Error);
            return;
        }

        try {
            const controller = new AbortController();

            const result = await WhatsAppApiService.sendMessage(
                {
                    type: WhatsAppMessageTypes.MESSAGE_TO_LEAD,
                    data: {
                        language: i18n.language === 'he' ? "he" : "en",
                        leadId: leadId
                    },
                },
                controller.signal
            );

            result.error ?
                showToast(t(`whatsappMessages.${result?.error?.response?.data?.code}`), ToastType.Warning)
                : showToast(t(`whatsappMessages.${result.code}`), ToastType.Success);

        } catch (error: any) {
            const code = error?.response?.data?.code || 'UNKNOWN_ERROR';
            showToast(t(`whatsappMessages.${code}`), ToastType.Error);
        }
    };


    return (
        <div>
            <div>
                <div className="row">
                    <div className="col-lg-12 grid-margin stretch-card">
                        <Button className='btn btn-secondary btn-fw' onClick={() => {
                            setShowInviteModal(true);
                            setFormData({  // Reset form data for a new lead
                                firstName: '',
                                lastName: '',
                                phone: '',
                                birthDate: '',
                                email: '',
                                source: ''
                            });
                        }}>
                            <UserPlus className="me-2" /> {t('add-lead')}
                        </Button>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-12 grid-margin stretch-card">
                        <div className="card">
                            <div className="card-body">
                                <h4 className="card-title">{t('lead-list')}</h4>
                                <div className="table-responsive">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>{t('first-name')}</th>
                                                <th>{t('last-name')}</th>
                                                <th>{t('phone-number')}</th>
                                                <th>{t('birth-date')}</th>
                                                <th>{t('email')}</th>
                                                <th>{t('source')}</th>
                                                <th>{t('actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {leads.map((lead, index) => (
                                                <tr key={index}>
                                                    <td>{lead.firstName}</td>
                                                    <td>{lead.lastName}</td>
                                                    <td>{lead.phone}</td>
                                                    <td>{lead.birthDate}</td>
                                                    <td>{lead.source}</td>
                                                    <td>{lead.email}</td>
                                                    <td>
                                                        <div className="template-demo">
                                                        <button type="button" onClick={() => handleEditLead(index)} className="btn btn-outline-warning btn-icon-text">
                                                            <i className="mdi  mdi-lead-pencil btn-icon-prepend"></i> {t('edit')}
                                                        </button>
                                                        <button type="button" onClick={() => handleDelete(index)} className="btn btn-outline-danger btn-icon-text">
                                                            <i className="mdi mdi-delete-variant btn-icon-prepend"></i> {t('delete')}
                                                        </button>
                                                        <button type="button" onClick={() => handleSendLeadMessage(index)} className="btn btn-outline-success btn-icon-text">
                                                            <i className="mdi mdi-whatsapp btn-icon-prepend"></i> {t('send-booking-link')}
                                                        </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showInviteModal} onHide={() => setShowInviteModal(false)} centered>
                <Modal.Header className="bg-dark text-white custom-border-header" closeButton closeVariant="white">
                    <Modal.Title>{t('add-lead')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-dark text-white">
                    <Form className="forms-sample">
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('first-name')}</Form.Label>
                            <Form.Control type="text" placeholder={t('first-name')} className="input-bg-dark text-white" name="firstName" value={formData.firstName} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('last-name')}</Form.Label>
                            <Form.Control type="text" placeholder={t('last-name')} className="input-bg-dark text-white" name="lastName" value={formData.lastName} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('phone-number')}</Form.Label>
                            <Form.Control type="tel" placeholder={t('phone-number')} className="input-bg-dark text-white" name="phone" value={formData.phone} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('birth-date')}</Form.Label>
                            <Form.Control type="date" placeholder={t('birth-date')} className="input-bg-dark text-white" name="birthDate" value={formData.birthDate} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('email')}</Form.Label>
                            <Form.Control type="email" placeholder={t('email')} className="input-bg-dark text-white" name="email" value={formData.email} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('source')}</Form.Label>
                            <Form.Control type="email" placeholder={t('source')} className="input-bg-dark text-white" name="source" value={formData.source} onChange={handleInputChange} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="bg-dark text-white custom-border-footer">
                    <Button variant="secondary" onClick={() => setShowInviteModal(false)} className="d-flex align-items-center">
                        <i className={`mdi mdi-close d-flex align-items-center ${isRtl ? 'ms-2' : 'me-2'}`}></i>
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddLead}
                        className="d-flex align-items-center ms-2 px-4"
                    >
                        <i className={`mdi mdi-content-save d-flex align-items-center ${isRtl ? 'ms-2' : 'me-2'}`}></i>
                        {t('add')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
                <Modal.Header className="bg-dark text-white custom-border-header" closeButton closeVariant="white">
                    <Modal.Title>{t('edit-lead')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-dark text-white">
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('first-name')}</Form.Label>
                            <Form.Control type="text" placeholder={t('first-name')} className="input-bg-dark text-white" name="firstName" value={formData.firstName} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('last-name')}</Form.Label>
                            <Form.Control type="text" placeholder={t('last-name')} className="input-bg-dark text-white" name="lastName" value={formData.lastName} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('phone-number')}</Form.Label>
                            <Form.Control type="tel" placeholder={t('phone-number')} className="input-bg-dark text-white" name="phone" value={formData.phone} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('birth-date')}</Form.Label>
                            <Form.Control type="date" placeholder={t('birth-date')} className="input-bg-dark text-white" name="birthDate" value={formData.birthDate} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('email')}</Form.Label>
                            <Form.Control type="email" placeholder={t('email')} className="input-bg-dark text-white" name="email" value={formData.email} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('source')}</Form.Label>
                            <Form.Control type="email" placeholder={t('source')} className="input-bg-dark text-white" name="source" value={formData.source} onChange={handleInputChange} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="bg-dark text-white custom-border-footer">
                    <Button variant="secondary" onClick={() => setShowEditModal(false)} className="d-flex align-items-center">
                        <i className={`mdi mdi-close d-flex align-items-center ${isRtl ? 'ms-2' : 'me-2'}`}></i>
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSaveEdit}
                        className="d-flex align-items-center ms-2 px-4"
                    >
                        <i className={`mdi mdi-content-save d-flex align-items-center ${isRtl ? 'ms-2' : 'me-2'}`}></i>
                        {t('save-changes')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <ConfirmModal
                show={showDelteConfirm}
                onCancel={cancelDelete}
                onConfirm={confirmDelete}
                title={t('delete-lead')}
                body={getDeleteConfirmationText()}
                confirmText={t('delete')}
                cancelText={t('cancel')}
            />

        </div>
    );
};

export default Leads;
